import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Camera, ImagePlus, Trash2, Eye, FileText, Package } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const BUCKET = 'recepcion-material-documentos';

export type TipoDoc = 'albaran' | 'material';

interface Documento {
  id: string;
  recepcion_id: string;
  tipo_documento: TipoDoc;
  nombre_archivo: string;
  url_archivo: string;
  tamano_archivo: number | null;
  created_at: string;
}

interface Props {
  recepcionId: string;
  tipo: TipoDoc;
  readonly?: boolean;
}

const getStoragePath = (urlOrPath: string): string | null => {
  if (!urlOrPath) return null;
  const marker = `/${BUCKET}/`;
  const idx = urlOrPath.indexOf(marker);
  if (idx >= 0) return urlOrPath.substring(idx + marker.length).split('?')[0];
  if (!urlOrPath.startsWith('http')) return urlOrPath;
  return null;
};

export const RecepcionMaterialDocumentos: React.FC<Props> = ({ recepcionId, tipo, readonly = false }) => {
  const [docs, setDocs] = useState<Documento[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const titulo = tipo === 'albaran' ? 'Albarán' : 'Materiales';
  const Icono = tipo === 'albaran' ? FileText : Package;

  const fetchDocs = async () => {
    const { data, error } = await (supabase.from('recepcion_material_documentos' as any) as any)
      .select('*')
      .eq('recepcion_id', recepcionId)
      .eq('tipo_documento', tipo)
      .order('created_at', { ascending: false });
    if (error) {
      console.error(error);
      return;
    }
    const docs = (data || []) as Documento[];
    setDocs(docs);
    const map: Record<string, string> = {};
    await Promise.all(
      docs.map(async (d) => {
        const path = getStoragePath(d.url_archivo);
        if (!path) return;
        const { data: signed } = await supabase.storage.from(BUCKET).createSignedUrl(path, 3600);
        if (signed?.signedUrl) map[d.id] = signed.signedUrl;
      }),
    );
    setSignedUrls(map);
  };

  useEffect(() => {
    if (recepcionId) fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recepcionId, tipo]);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const { compressImageForUpload } = await import('@/lib/imageUtils');
      for (const file of files) {
        const uploadFile = await compressImageForUpload(file);
        const ext = uploadFile.name.split('.').pop();
        const path = `${recepcionId}/${tipo}/${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from(BUCKET)
          .upload(path, uploadFile, { contentType: uploadFile.type });
        if (upErr) throw upErr;
        const { error: insErr } = await (supabase.from('recepcion_material_documentos' as any) as any)
          .insert({
            recepcion_id: recepcionId,
            tipo_documento: tipo,
            nombre_archivo: file.name,
            url_archivo: path,
            tamano_archivo: uploadFile.size,
          });
        if (insErr) throw insErr;
      }
      toast({ title: 'Foto subida', description: `${files.length} foto(s) añadida(s)` });
      await fetchDocs();
    } catch (e: any) {
      console.error(e);
      toast({ title: 'Error', description: e.message || 'Error al subir', variant: 'destructive' });
    } finally {
      setUploading(false);
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const handleDelete = async (doc: Documento) => {
    const path = getStoragePath(doc.url_archivo);
    if (path) await supabase.storage.from(BUCKET).remove([path]);
    await (supabase.from('recepcion_material_documentos' as any) as any).delete().eq('id', doc.id);
    toast({ title: 'Eliminada' });
    fetchDocs();
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icono className="h-4 w-4" />
          {titulo}
          {docs.length > 0 && <Badge variant="secondary">{docs.length}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!readonly && (
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-14 flex flex-col items-center justify-center gap-1"
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploading}
            >
              <Camera className="h-5 w-5" />
              <span className="text-xs">Hacer foto</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-14 flex flex-col items-center justify-center gap-1"
              onClick={() => galleryInputRef.current?.click()}
              disabled={uploading}
            >
              <ImagePlus className="h-5 w-5" />
              <span className="text-xs">Galería</span>
            </Button>
            <input
              type="file"
              ref={cameraInputRef}
              onChange={handleUpload}
              className="hidden"
              accept="image/*"
              capture="environment"
            />
            <input
              type="file"
              ref={galleryInputRef}
              onChange={handleUpload}
              className="hidden"
              accept="image/*"
              multiple
            />
          </div>
        )}

        {uploading && (
          <div className="text-center text-sm text-muted-foreground">Subiendo…</div>
        )}

        {docs.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground text-sm">
            Sin fotos de {titulo.toLowerCase()}
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {docs.map((d) => (
              <div key={d.id} className="relative group border rounded-md overflow-hidden">
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="w-full aspect-square bg-muted">
                      {signedUrls[d.id] && (
                        <img
                          src={signedUrls[d.id]}
                          alt={d.nombre_archivo}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl">
                    <DialogHeader>
                      <DialogTitle>{d.nombre_archivo}</DialogTitle>
                    </DialogHeader>
                    {signedUrls[d.id] && (
                      <img
                        src={signedUrls[d.id]}
                        alt={d.nombre_archivo}
                        className="max-w-full max-h-[70vh] mx-auto object-contain"
                      />
                    )}
                  </DialogContent>
                </Dialog>
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  {signedUrls[d.id] && (
                    <Button
                      variant="secondary"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => window.open(signedUrls[d.id], '_blank', 'noopener,noreferrer')}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {!readonly && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="icon" className="h-7 w-7">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar foto?</AlertDialogTitle>
                          <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(d)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
